/**
 * The five-minute window the live counters replay, and the only definition of it.
 *
 * It lives in its own module, with no imports, because BOTH ends need it now: the browser used to
 * compute the key and send it as query input, and the server cache keyed on whatever arrived. That is
 * what made the query key change 288 times a day — a new key has no data, so `useSuspenseQuery` went
 * pending and the counters fell back to their Suspense boundary every five minutes (TEC-3832), and the
 * abandoned keys accumulated in the persisted cache until a return visit refetched them all in one
 * 16 KB tRPC GET (TEC-3807/TEC-3810).
 *
 * The window is now derived on the SERVER, inside the cache key, so the client's query key carries only
 * the mode and never changes. The rollover is a refetch of a key that already holds data, which keeps
 * `status: 'success'` and never re-suspends.
 */

const WINDOW_MS = 5 * 60_000;

/** The current 5-minute window key (e.g. "2026-02-13T14:10"), floored to the window start. */
export function currentWindowKey(): string {
	const windowStart = Math.floor(Date.now() / WINDOW_MS) * WINDOW_MS;
	return new Date(windowStart).toISOString().slice(0, 16);
}

/**
 * How often the client asks for the window again. Deliberately the window length itself: the server
 * hands out six minutes of donations for a five-minute window, so one refetch per window arrives with
 * a minute of replay still in hand and the counter never runs dry between batches.
 */
export const WINDOW_REFETCH_MS = WINDOW_MS;
